import React, { useMemo } from "react";

const formatCLP = (n) =>
  n.toLocaleString("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 2 });

const MetricCard = ({ label, value, formula }) => (
  <div className="relative group bg-[#1f2a40] rounded p-4 text-center flex-1 shadow-sm border border-[#2c3650]">
    <div className="text-sm text-gray-400 mb-1">{label}</div>
    <div className="text-xl font-bold text-white">{value}</div>
    <div className="absolute hidden group-hover:block bg-black text-white text-xs rounded px-2 py-1 -bottom-10 left-1/2 transform -translate-x-1/2 w-max max-w-[220px] z-10">
      {formula}
    </div>
  </div>
);

const ChartPage3 = ({ data }) => {
  const metrics = useMemo(() => {
    if (!data || data.length === 0) return null;

    // Cálculo diferencias (entrada/salida)
    const getDiff = (t) =>
      t.trade_type === "buy"
        ? t.close_price - t.entry_price
        : t.entry_price - t.close_price;

    const wins = data.filter((t) => t.profit > 0);
    const losses = data.filter((t) => t.profit < 0);

    const pAcierto = wins.length / data.length;
    const pPerdida = 1 - pAcierto;

    const avgWinDiff =
      wins.reduce((sum, t) => sum + getDiff(t), 0) / (wins.length || 1);
    const avgLossDiff =
      losses.reduce((sum, t) => sum + getDiff(t), 0) / (losses.length || 1);

    const wlRatio = Math.abs(avgWinDiff / (avgLossDiff || 1));

    // Nueva fórmula de esperanza matemática
    const esperanzaMatematica = pAcierto * wlRatio - pPerdida * 1;

    // Profit factor normal
    const sumWinsProfit = wins.reduce((sum, t) => sum + t.profit, 0);
    const sumLossesProfit = losses.reduce((sum, t) => sum + t.profit, 0);
    
    const profitFactor = Math.abs(sumWinsProfit) / (Math.abs(sumLossesProfit) || 1);

    // Expectancy Ratio y Payoff Ratio con diferencias
    const expectancyRatio = Math.abs(
      (avgWinDiff * pAcierto) / ((avgLossDiff * pPerdida) || 1)
    );
    const payoffRatio = wlRatio;

    // Rachas
    let maxWinStreak = 0,
      maxLossStreak = 0;
    let currentWinStreak = 0,
      currentLossStreak = 0;
    data.forEach((t) => {
      if (t.profit > 0) {
        currentWinStreak += 1;
        currentLossStreak = 0;
      } else if (t.profit < 0) {
        currentLossStreak += 1;
        currentWinStreak = 0;
      } else {
        currentWinStreak = 0;
        currentLossStreak = 0;
      }
      if (currentWinStreak > maxWinStreak) maxWinStreak = currentWinStreak;
      if (currentLossStreak > maxLossStreak) maxLossStreak = currentLossStreak;
    });

    return {
      winRate: (pAcierto * 100).toFixed(1) + " %",
      esperanzaMatematica: esperanzaMatematica.toFixed(3),
      profitFactor: profitFactor.toFixed(2),
      expectancyRatio: expectancyRatio.toFixed(2),
      payoffRatio: payoffRatio.toFixed(2),
      maxWinStreak,
      maxLossStreak,
      totalWins: formatCLP(sumWinsProfit),
      totalLosses: formatCLP(sumLossesProfit),
      totalProfit: formatCLP(sumWinsProfit + sumLossesProfit),
    };
  }, [data]);

  if (!metrics) {
    return (
      <div className="text-gray-400 mt-4">
        No hay datos para mostrar métricas.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
      <MetricCard
        label="Win Rate"
        value={metrics.winRate}
        formula="(Trades ganadores / Total trades) × 100"
      />
      <MetricCard
        label="Esperanza matemática"
        value={metrics.esperanzaMatematica}
        formula="(pAcierto × W/L) - (pPerdida × 1)"
      />
      <MetricCard
        label="Profit Factor"
        value={metrics.profitFactor}
        formula="Suma Ganancias / Suma Pérdidas Absolutas"
      />
      <MetricCard
        label="Expectancy Ratio"
        value={metrics.expectancyRatio}
        formula="(ΔPrecioGanancia × WinRate) / (ΔPrecioPérdida × LossRate)"
      />
      <MetricCard
        label="Payoff Ratio"
        value={metrics.payoffRatio}
        formula="Promedio ΔPrecioGanancia / |Promedio ΔPrecioPérdida|"
      />
      <MetricCard
        label="Máx. racha ganadora"
        value={metrics.maxWinStreak}
        formula="Mayor secuencia consecutiva de trades con profit > 0"
      />
      <MetricCard
        label="Máx. racha perdedora"
        value={metrics.maxLossStreak}
        formula="Mayor secuencia consecutiva de trades con profit < 0"
      />
      <MetricCard
        label="Suma de Ganancias CLP"
        value={metrics.totalWins}
        formula="Suma de todos los profits positivos"
      />
      <MetricCard
        label="Suma de Pérdidas CLP"
        value={metrics.totalLosses}
        formula="Suma de todos los profits negativos (absolutos o netos)"
      />
      <MetricCard
        label="Ganancia Total CLP"
        value={metrics.totalProfit}
        formula="Ganancias Totales + Pérdidas Totales"
      />
    </div>
  );
};

export default ChartPage3;
